import * as THREE from 'three';
import { Enemy } from './Enemy';
import { Grunt } from './Grunt';
import { Archer } from './Archer';
import { Brute } from './Brute';
import { Boss } from './Boss';
import { EnemyType } from './EnemyType';

/**
 * Manages enemy spawning and lifecycle
 */
export class EnemyManager {
  private scene: THREE.Scene;
  private enemies: Enemy[] = [];
  private maxEnemies: number = 20;
  private spawnPoints: THREE.Vector3[] = [];
  private waveSpawnPoints: Record<number, THREE.Vector3[]> = {}; // Spawn points for each wave
  private currentWaveNumber: number = 0;
  private playerPosition: THREE.Vector3 = new THREE.Vector3();
  private camera: THREE.Camera | null = null;
  private onEnemyDefeatedCallbacks: ((enemy: Enemy) => void)[] = [];
  
  // Collision parameters
  private collisionEnabled: boolean = true;
  private minEnemyDistance: number = 1.5; // Minimum distance between enemies
  private pushForce: number = 2.0; // Force to apply when pushing enemies apart
  
  /**
   * Creates a new EnemyManager
   * @param scene The scene to add enemies to
   */
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    
    // Set up default spawn points in a circle around the origin
    this.setupDefaultSpawnPoints();
  }
  
  /**
   * Sets up default spawn points in a circle around the origin
   */
  private setupDefaultSpawnPoints(): void {
    const radius = 80; // Increased radius to spawn enemies further from the castle
    const count = 8;
    
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const x = Math.sin(angle) * radius;
      const z = Math.cos(angle) * radius;
      this.spawnPoints.push(new THREE.Vector3(x, 0, z));
    }
  }
  
  /**
   * Creates spawn points for a specific wave
   * @param waveNumber The wave number
   */
  public createWaveSpawnPoints(waveNumber: number): void {
    // If we already have spawn points for this wave, don't recreate them
    if (this.waveSpawnPoints[waveNumber]) {
      return;
    }
    
    const spawnPoints: THREE.Vector3[] = [];
    
    // Use a consistent seed based on the wave number for deterministic spawn locations
    const seed = waveNumber * 12345;
    
    // Create 2-3 spawn clusters for this wave
    const clusterCount = 2 + (waveNumber % 2); // Alternates between 2 and 3 clusters
    
    for (let i = 0; i < clusterCount; i++) {
      // Use a deterministic angle based on the wave number and cluster index
      const baseAngle = ((seed + i * 67) % 360) / 180 * Math.PI;
      
      // Distance increases with wave number (min 80, max 120)
      const baseDistance = 80 + Math.min(40, waveNumber * 5);
      
      // Create a cluster of spawn points
      const pointsInCluster = 3 + Math.min(5, Math.floor(waveNumber / 2));
      
      for (let j = 0; j < pointsInCluster; j++) {
        // Create points in a small arc around the base angle
        const angleVariation = (((seed + j * 123) % 40) - 20) / 180 * Math.PI; // ±20 degrees
        const angle = baseAngle + angleVariation;
        
        // Add some variation to the distance
        const distanceVariation = ((seed + j * 45) % 20) - 10; // ±10 units
        const distance = baseDistance + distanceVariation;
        
        const x = Math.sin(angle) * distance;
        const z = Math.cos(angle) * distance;
        
        spawnPoints.push(new THREE.Vector3(x, 0, z));
      }
    }
    
    // Store the spawn points for this wave
    this.waveSpawnPoints[waveNumber] = spawnPoints;
    
    console.log(`Created ${spawnPoints.length} spawn points for wave ${waveNumber}`);
  }
  
  /**
   * Sets the current wave number
   * @param waveNumber The current wave number
   */
  public setCurrentWave(waveNumber: number): void {
    this.currentWaveNumber = waveNumber;
    
    // Create spawn points for this wave if they don't exist
    this.createWaveSpawnPoints(waveNumber);
  }
  
  /**
   * Adds a spawn point
   * @param position The spawn point position
   */
  public addSpawnPoint(position: THREE.Vector3): void {
    this.spawnPoints.push(position.clone());
  }
  
  /**
   * Clears all spawn points
   */
  public clearSpawnPoints(): void {
    this.spawnPoints = [];
  }
  
  /**
   * Sets the callback for when an enemy is defeated
   * @param callback The callback function
   */
  public setOnEnemyDefeated(callback: (enemy: Enemy) => void): void {
    this.onEnemyDefeatedCallbacks.push(callback);
  }
  
  /**
   * Clears all enemy defeated callbacks
   */
  public clearEnemyDefeatedCallbacks(): void {
    this.onEnemyDefeatedCallbacks = [];
  }
  
  /**
   * Sets the maximum number of enemies
   * @param max The maximum number of enemies
   */
  public setMaxEnemies(max: number): void {
    this.maxEnemies = max;
  }
  
  /**
   * Sets the camera for healthbar facing
   * @param camera The camera to use
   */
  public setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }
  
  /**
   * Spawns an enemy of the specified type
   * @param type The type of enemy to spawn
   * @param position Optional position override
   * @returns The spawned enemy
   */
  public spawnEnemy(type: EnemyType, position?: THREE.Vector3): Enemy | null {
    // Check if we've reached the maximum number of enemies
    if (this.enemies.length >= this.maxEnemies) {
      return null;
    }
    
    // Get spawn position
    let spawnPosition: THREE.Vector3;
    
    if (position) {
      // Use provided position
      spawnPosition = position.clone();
    } else if (this.waveSpawnPoints[this.currentWaveNumber] && this.waveSpawnPoints[this.currentWaveNumber].length > 0) {
      // Use wave-specific spawn points
      const spawnPoints = this.waveSpawnPoints[this.currentWaveNumber];
      const randomIndex = Math.floor(Math.random() * spawnPoints.length);
      spawnPosition = spawnPoints[randomIndex].clone();
      
      // Add small randomness to the spawn position
      spawnPosition.x += (Math.random() - 0.5) * 5;
      spawnPosition.z += (Math.random() - 0.5) * 5;
    } else if (this.spawnPoints.length > 0) {
      // Fall back to default spawn points
      const randomIndex = Math.floor(Math.random() * this.spawnPoints.length);
      spawnPosition = this.spawnPoints[randomIndex].clone();
      
      // Add some randomness to the spawn position
      spawnPosition.x += (Math.random() - 0.5) * 5;
      spawnPosition.z += (Math.random() - 0.5) * 5;
    } else {
      // Default to a random position around the player
      const angle = Math.random() * Math.PI * 2;
      const distance = 50 + Math.random() * 30; // Increased distance
      spawnPosition = new THREE.Vector3(
        this.playerPosition.x + Math.sin(angle) * distance,
        0,
        this.playerPosition.z + Math.cos(angle) * distance
      );
    }
    
    // Create the enemy based on type
    let enemy: Enemy;
    
    switch (type) {
      case EnemyType.GRUNT:
        enemy = new Grunt(spawnPosition, this.scene);
        break;
        
      case EnemyType.ARCHER:
        enemy = new Archer(spawnPosition, this.scene);
        break;
        
      case EnemyType.BRUTE:
        enemy = new Brute(spawnPosition, this.scene);
        break;
        
      case EnemyType.BOSS:
        enemy = new Boss(spawnPosition, this.scene);
        break;
        
      default:
        enemy = new Grunt(spawnPosition, this.scene);
        break;
    }
    
    // Activate the enemy
    enemy.activate();
    
    // Add to list
    this.enemies.push(enemy);
    
    return enemy;
  }
  
  /**
   * Spawns multiple enemies of the specified type
   * @param type The type of enemy to spawn
   * @param count The number of enemies to spawn
   * @returns The spawned enemies
   */
  public spawnEnemies(type: EnemyType, count: number): Enemy[] {
    const enemies: Enemy[] = [];
    
    for (let i = 0; i < count; i++) {
      const enemy = this.spawnEnemy(type);
      if (enemy) {
        enemies.push(enemy);
      }
    }
    
    return enemies;
  }
  
  /**
   * Spawns a boss enemy
   * @param position Optional position override
   * @returns The spawned boss
   */
  public spawnBoss(position?: THREE.Vector3): Enemy | null {
    return this.spawnEnemy(EnemyType.BOSS, position);
  }
  
  /**
   * Enables or disables collision detection between enemies
   * @param enabled Whether collision detection is enabled
   */
  public setCollisionEnabled(enabled: boolean): void {
    this.collisionEnabled = enabled;
  }
  
  /**
   * Sets the minimum distance between enemies
   * @param distance The minimum distance
   */
  public setMinEnemyDistance(distance: number): void {
    this.minEnemyDistance = distance;
  }
  
  /**
   * Sets the push force for enemy collisions
   * @param force The push force
   */
  public setPushForce(force: number): void {
    this.pushForce = force;
  }
  
  /**
   * Updates all enemies
   * @param deltaTime Time since last update in seconds
   * @param playerPosition The current player position
   */
  public update(deltaTime: number, playerPosition: THREE.Vector3): void {
    // Store player position
    this.playerPosition.copy(playerPosition);
    
    // Update each enemy
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      
      // Update enemy
      enemy.update(deltaTime, playerPosition);
      
      // Update healthbar facing
      if (this.camera) {
        enemy.updateHealthbarFacing(this.camera);
      }
      
      // Check if enemy is dead and animation is complete
      if (enemy.isDying()) {
        // If the enemy is dead and either has no parent or has been dead for more than 3 seconds
        if (enemy.getObject().parent === null) {
          // Call all the defeated callbacks
          for (const callback of this.onEnemyDefeatedCallbacks) {
            callback(enemy);
          }
          
          // Remove from list
          this.enemies.splice(i, 1);
          
          // Dispose of resources
          enemy.dispose();
        }
      }
    }
    
    // Handle enemy collisions if enabled
    if (this.collisionEnabled) {
      this.handleEnemyCollisions(deltaTime);
    }
  }
  
  /**
   * Handles collisions between enemies
   * @param deltaTime Time since last update in seconds
   */
  private handleEnemyCollisions(deltaTime: number): void {
    // Skip if there are less than 2 enemies
    if (this.enemies.length < 2) return;
    
    // Check each pair of enemies for collisions
    for (let i = 0; i < this.enemies.length; i++) {
      const enemyA = this.enemies[i];
      
      // Skip dead or inactive enemies
      if (enemyA.isDying() || !enemyA.isActiveEnemy()) continue;
      
      const posA = enemyA.getPosition();
      
      for (let j = i + 1; j < this.enemies.length; j++) {
        const enemyB = this.enemies[j];
        
        // Skip dead or inactive enemies
        if (enemyB.isDying() || !enemyB.isActiveEnemy()) continue;
        
        const posB = enemyB.getPosition();
        
        // Calculate distance between enemies (only in XZ plane)
        const dx = posB.x - posA.x;
        const dz = posB.z - posA.z;
        const distanceSquared = dx * dx + dz * dz;
        
        // Get the combined collision radius (based on enemy types)
        const radiusA = this.getEnemyRadius(enemyA);
        const radiusB = this.getEnemyRadius(enemyB);
        const minDistance = radiusA + radiusB;
        const minDistanceSquared = minDistance * minDistance;
        
        // Check if enemies are too close
        if (distanceSquared < minDistanceSquared) {
          // Calculate the overlap distance
          const distance = Math.sqrt(distanceSquared);
          const overlap = minDistance - distance;
          
          // Calculate normalized direction vector
          const dirX = dx / distance;
          const dirZ = dz / distance;
          
          // Calculate push strength based on overlap and push force
          const pushStrength = overlap * this.pushForce * deltaTime;
          
          // Apply push forces in opposite directions
          // Larger enemies are pushed less, smaller enemies are pushed more
          const massA = this.getEnemyMass(enemyA);
          const massB = this.getEnemyMass(enemyB);
          const totalMass = massA + massB;
          
          // Calculate push amounts based on relative mass
          const pushA = pushStrength * (massB / totalMass);
          const pushB = pushStrength * (massA / totalMass);
          
          // Apply push to enemy A (move away from B)
          const objA = enemyA.getObject();
          objA.position.x -= dirX * pushA;
          objA.position.z -= dirZ * pushA;
          
          // Apply push to enemy B (move away from A)
          const objB = enemyB.getObject();
          objB.position.x += dirX * pushB;
          objB.position.z += dirZ * pushB;
          
          // Update the enemy's internal position to match the object position
          enemyA.updatePositionFromObject();
          enemyB.updatePositionFromObject();
        }
      }
    }
  }
  
  /**
   * Gets the collision radius for an enemy based on its type
   * @param enemy The enemy
   * @returns The collision radius
   */
  private getEnemyRadius(enemy: Enemy): number {
    switch (enemy.getType()) {
      case EnemyType.GRUNT:
        return 0.5;
      case EnemyType.ARCHER:
        return 0.4;
      case EnemyType.BRUTE:
        return 0.7;
      case EnemyType.BOSS:
        return 1.2;
      default:
        return 0.5;
    }
  }
  
  /**
   * Gets the mass for an enemy based on its type (for collision response)
   * @param enemy The enemy
   * @returns The mass
   */
  private getEnemyMass(enemy: Enemy): number {
    switch (enemy.getType()) {
      case EnemyType.GRUNT:
        return 1.0;
      case EnemyType.ARCHER:
        return 0.8;
      case EnemyType.BRUTE:
        return 2.0;
      case EnemyType.BOSS:
        return 5.0;
      default:
        return 1.0;
    }
  }
  
  /**
   * Gets all active enemies
   * @returns The active enemies
   */
  public getEnemies(): Enemy[] {
    return this.enemies;
  }
  
  /**
   * Gets the number of active enemies
   * @returns The number of active enemies
   */
  public getEnemyCount(): number {
    // Only count active, non-dying enemies
    return this.enemies.filter(enemy => enemy.isActiveEnemy() && !enemy.isDying()).length;
  }
  
  /**
   * Checks if there are any active enemies
   * @returns True if there are active enemies
   */
  public hasEnemies(): boolean {
    return this.enemies.length > 0;
  }
  
  /**
   * Clears all enemies
   */
  public clearEnemies(): void {
    // Dispose of all enemies
    for (const enemy of this.enemies) {
      enemy.dispose();
    }
    
    // Clear the list
    this.enemies = [];
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    this.clearEnemies();
    this.spawnPoints = [];
  }
} 